import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Skeleton,
    Stack,
    alpha,
    useTheme,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Button, FoodsIcon, FoodsSelect, FoodsTooltip, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { PostList } from "src/api/client-api/post/types/postList.types";
import { InvitationProjection, InvitationScore } from "../ActionPlanInvitation/ActionPlanInvitationDialog";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { getScorePrecision } from "src/context/Scores/provider";
import { formatImpact, getImpactColor } from "src/context/Scores/utils/impactDisplay";
import { useCreationTakingLonger } from "../ActionPlanInvitation/useCreationTakingLonger";
import {
    EstimateErrorAlert,
    InvitationNoticeAlert,
    type InvitationNotice,
} from "../ActionPlanInvitation/TemplateUseAlerts";

export type CreationState = "choose" | "checking" | "proposed" | "empty" | "unavailable";

type Props = {
    posts: PostList[];
    selectedPost?: PostList;
    onSelectPost: (post: PostList | null) => void;
    targetYear?: number;
    state: CreationState;
    scores: InvitationScore[];
    projection: InvitationProjection;
    busy: boolean;
    creationType?: "adopt" | "create-own";
    canCreateOwn: boolean;
    failed: boolean;
    failureUncertain: boolean;
    adoptDisabled?: boolean;
    notice?: InvitationNotice;
    onClose: () => void;
    onCreateOwn: () => void;
    onAdopt: () => void;
    onRetry: () => void;
};

export const ActionPlanCreationView = (props: Props) => {
    const {
        posts,
        selectedPost,
        onSelectPost,
        targetYear,
        state,
        scores,
        projection,
        busy,
        creationType,
        canCreateOwn,
        failed,
        failureUncertain,
        adoptDisabled,
        notice,
        onClose,
        onCreateOwn,
        onAdopt,
        onRetry,
    } = props;
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const takingLonger = useCreationTakingLonger(busy);
    const close = () => {
        if (!busy) onClose();
    };
    const createOwnUnavailable = !selectedPost || !canCreateOwn;
    const planExists = projection.status === "conflict";

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            onClose={close}
            disableEscapeKeyDown={busy}
            aria-labelledby="action-plan-creation-title"
            PaperProps={{ sx: { maxWidth: theme.spacing(89) } }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: theme.spacing(3), py: theme.spacing(2), borderBottom: 1, borderColor: "divider" }}
            >
                <Typography
                    variant="h5"
                    id="action-plan-creation-title"
                    sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
                >
                    {t("action_plan.creation.title", "Define my action plan")}
                </Typography>
                <IconButton onClick={close} disabled={busy} aria-label={t("action_plan.invitation.close", "Close")}>
                    <Close />
                </IconButton>
            </Stack>
            <DialogContent sx={{ px: theme.spacing(4), pt: theme.spacing(2), pb: theme.spacing(4) }}>
                <Stack gap={theme.spacing(3)}>
                    <Stack gap={theme.spacing(0.5)}>
                        <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                            <Box component="label" htmlFor="action-plan-start-year">
                                <Typography variant="body-bold">
                                    {t("action_plan.creation.start_year", "Starting year of your action plan")}
                                </Typography>
                            </Box>
                            <FoodsTooltip
                                title={t(
                                    "action_plan.creation.start_year_tooltip",
                                    "The starting year's questionnaire must be validated. If a year is missing, check that its questionnaire is complete, then validate it.",
                                )}
                            >
                                <IconButton
                                    size="small"
                                    aria-label={t(
                                        "action_plan.creation.start_year_tooltip",
                                        "The starting year's questionnaire must be validated. If a year is missing, check that its questionnaire is complete, then validate it.",
                                    )}
                                >
                                    <FoodsIcon icon="information" size={2} />
                                </IconButton>
                            </FoodsTooltip>
                        </Stack>
                        <FoodsSelect
                            id="action-plan-start-year"
                            selectedOption={selectedPost ?? null}
                            options={posts}
                            onChange={onSelectPost}
                            disabled={busy}
                            getId={(post) => post.id}
                            getName={(post) => post.campaign?.label ?? ""}
                            closeOnSelect
                            disableSearch
                            textOptions={{
                                placeholder: t("action_plan.creation.start_year_placeholder", "Select a campaign"),
                                searchPlaceholder: t("component.select.searchplaceholder"),
                                emptyOptionsText: t("component.select.nooptions"),
                                defaultValueText: t("component.select.defaultvalue"),
                                noResult: t("component.select.noresultfound"),
                            }}
                            BoxProps={{
                                width: "100%",
                                maxWidth: theme.spacing(37.5),
                                ...(!selectedPost && { "& .MuiTypography-root": { color: "text.disabled" } }),
                            }}
                            BoxOptionsProps={{ width: theme.spacing(37.5), maxWidth: "100%" }}
                        />
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                        <Typography variant="body-bold">
                            {t("action_plan.creation.target_year", "Target year:")}
                        </Typography>
                        <Typography component="p" variant="body-regular">
                            {targetYear ?? "—"}
                        </Typography>
                        <FoodsTooltip
                            title={t(
                                "action_plan.creation.target_year_tooltip",
                                "The target year is predefined for this action plan.",
                            )}
                        >
                            <IconButton
                                size="small"
                                aria-label={t(
                                    "action_plan.creation.target_year_tooltip",
                                    "The target year is predefined for this action plan.",
                                )}
                            >
                                <FoodsIcon icon="information" size={2} />
                            </IconButton>
                        </FoodsTooltip>
                    </Stack>
                    {state !== "empty" && (
                        <Stack
                            gap={theme.spacing(3)}
                            sx={{ pt: theme.spacing(3), borderTop: 1, borderColor: "divider" }}
                        >
                            {state === "choose" && (
                                <Typography component="p" variant="body-regular" sx={{ color: "text.secondary" }}>
                                    {t(
                                        "action_plan.creation.choose_campaign",
                                        "Choose a starting campaign to see the actions we recommend.",
                                    )}
                                </Typography>
                            )}
                            {state === "checking" && (
                                <Typography component="p" variant="body-regular" role="status">
                                    {t("action_plan.invitation.loading_estimate", "Loading estimate…")}
                                </Typography>
                            )}
                            {state === "unavailable" && (
                                <Typography component="p" variant="body-regular" role="status">
                                    {t(
                                        "action_plan.invitation.estimates_unavailable",
                                        "Estimates currently unavailable",
                                    )}
                                </Typography>
                            )}
                            {state === "proposed" && (
                                <>
                                    {projection.status !== "all-dropped" && !planExists && (
                                        <Typography component="p" variant="body-regular">
                                            {projection.status === "error" ?
                                                t(
                                                    "action_plan.invitation.recommended_without_estimate",
                                                    "Based on this, we have prepared an action plan with the actions recommended for you, with {{targetYear}} as the target year.",
                                                    { targetYear },
                                                )
                                            :   t(
                                                    "action_plan.creation.results_intro",
                                                    "Based on this, we have prepared an action plan with the actions recommended for you, aiming for the following results by {{targetYear}}:",
                                                    { targetYear },
                                                )
                                            }
                                        </Typography>
                                    )}
                                    {projection.status === "all-dropped" ?
                                        <Typography component="p" variant="body-regular" role="status">
                                            {t(
                                                "action_plan.invitation.all_actions_dropped",
                                                "Well done: your results already exceed what our proposed actions would achieve. You can still add your own actions.",
                                            )}
                                        </Typography>
                                    : projection.status === "error" ?
                                        <EstimateErrorAlert reason={projection.reason} onRetry={onRetry} />
                                    : projection.status === "conflict" ?
                                        <Alert
                                            severity="warning"
                                            role="status"
                                            sx={{
                                                border: 1,
                                                borderColor: "warning.main",
                                                borderRadius: 1,
                                                backgroundColor: alpha(theme.palette.warning.main, 0.04),
                                            }}
                                        >
                                            {t(
                                                "action_plan.invitation.plan_exists",
                                                "An action plan already exists for this diagnostic. Close this window to view it.",
                                            )}
                                        </Alert>
                                    :   <Stack gap={theme.spacing(1.5)} sx={{ mt: theme.spacing(0.75) }}>
                                            {scores.map((score) => {
                                                const value =
                                                    projection.status === "success" ?
                                                        (projection.values[score.id] ?? null)
                                                    :   null;
                                                const precision = getScorePrecision(score);
                                                const color =
                                                    (value === null ? undefined : (
                                                        getImpactColor(value, precision, score.improvement, theme)
                                                    )) ?? theme.palette.text.secondary;
                                                return (
                                                    <Stack
                                                        key={`${score.id}-creation-score`}
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        flexWrap="wrap"
                                                        gap={theme.spacing(2)}
                                                        sx={{
                                                            px: theme.spacing(2),
                                                            py: theme.spacing(1.5),
                                                            border: 1,
                                                            borderColor: "divider",
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Typography component="p" variant="body-regular">
                                                            {score.title}
                                                        </Typography>
                                                        {projection.status === "loading" ?
                                                            <Skeleton
                                                                width={theme.spacing(15)}
                                                                aria-label={t(
                                                                    "action_plan.invitation.loading_estimate",
                                                                    "Loading estimate…",
                                                                )}
                                                            />
                                                        :   <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                gap={theme.spacing(1)}
                                                                sx={{ color, flexShrink: 0 }}
                                                            >
                                                                <Typography
                                                                    component="p"
                                                                    variant="body-regular"
                                                                    sx={{ color }}
                                                                >
                                                                    {value === null ?
                                                                        <span
                                                                            aria-label={t(
                                                                                "action_plan.invitation.not_available",
                                                                                "Not available",
                                                                            )}
                                                                        >
                                                                            —
                                                                        </span>
                                                                    :   <ApproximateImpactTooltip
                                                                            value={value}
                                                                            precision={precision}
                                                                            unit={score.unit?.abbreviation ?? ""}
                                                                        >
                                                                            {`${formatImpact(value, precision, i18n.language, { signDisplay: "exceptZero" })} ${score.unit?.abbreviation ?? ""}`}
                                                                        </ApproximateImpactTooltip>
                                                                    }
                                                                </Typography>
                                                            </Stack>
                                                        }
                                                    </Stack>
                                                );
                                            })}
                                        </Stack>
                                    }
                                </>
                            )}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: theme.spacing(4), py: theme.spacing(3), borderTop: 1, borderColor: "divider" }}>
                <Stack gap={theme.spacing(1.5)} width="100%">
                    {notice && <InvitationNoticeAlert notice={notice} />}
                    {failed && !notice && (
                        <Alert
                            severity="error"
                            icon={<WarningAmberOutlined />}
                            sx={{
                                border: 1,
                                borderColor: "error.main",
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.error.main, 0.04),
                            }}
                        >
                            {failureUncertain ?
                                t(
                                    "action_plan.invitation.creation_unconfirmed",
                                    "We couldn't confirm whether your action plan was created. You can try again.",
                                )
                            :   t(
                                    "action_plan.invitation.creation_failed_confirmed",
                                    "We couldn't create your action plan. Nothing was saved; you can try again.",
                                )
                            }
                        </Alert>
                    )}
                    {state === "empty" ?
                        <Stack
                            direction={{ xs: "column-reverse", sm: "row" }}
                            justifyContent="space-between"
                            width="100%"
                            gap={theme.spacing(2)}
                        >
                            <Button
                                variant="text"
                                startIcon={<FoodsIcon icon="close" size={2} />}
                                disabled={busy}
                                onClick={close}
                            >
                                {t("action_plan.creation.cancel", "Cancel")}
                            </Button>
                            <Button
                                variant="primary"
                                endIcon={<FoodsIcon icon="check" size={2} />}
                                loading={busy && creationType === "create-own"}
                                loadingPosition="start"
                                disabled={createOwnUnavailable || (busy && creationType !== "create-own")}
                                onClick={onCreateOwn}
                            >
                                {busy && creationType === "create-own" ?
                                    t("action_plan.invitation.creating_plan", "Creating your action plan…")
                                : failed ?
                                    t("action_plan.invitation.retry", "Try again")
                                :   t("action_plan.creation.confirm", "Confirm")}
                            </Button>
                        </Stack>
                    :   <>
                            {state === "unavailable" && (
                                <Button variant="primary" fullWidth disabled={busy} onClick={onRetry}>
                                    {t("action_plan.invitation.retry", "Try again")}
                                </Button>
                            )}
                            {state !== "unavailable" && (
                                <Button
                                    variant="primary"
                                    fullWidth
                                    loading={busy && creationType === "adopt"}
                                    loadingPosition="start"
                                    disabled={
                                        state !== "proposed" ||
                                        planExists ||
                                        adoptDisabled ||
                                        (busy && creationType !== "adopt")
                                    }
                                    onClick={onAdopt}
                                >
                                    {busy && creationType === "adopt" ?
                                        t("action_plan.invitation.creating_plan", "Creating your action plan…")
                                    : failed && creationType === "adopt" ?
                                        t("action_plan.invitation.retry", "Try again")
                                    :   t("action_plan.creation.view_proposed", "View proposed actions")}
                                </Button>
                            )}
                            <Button
                                variant="secondary"
                                fullWidth
                                loading={busy && creationType === "create-own"}
                                loadingPosition="start"
                                disabled={createOwnUnavailable || planExists || (busy && creationType !== "create-own")}
                                onClick={onCreateOwn}
                            >
                                {busy && creationType === "create-own" ?
                                    t("action_plan.invitation.creating_plan", "Creating your action plan…")
                                : failed && creationType === "create-own" ?
                                    t("action_plan.invitation.retry", "Try again")
                                :   t("action_plan.creation.create_own", "Create my own plan")}
                            </Button>
                            <Button variant="text" fullWidth disabled={busy} onClick={close}>
                                {t("action_plan.creation.decide_later", "Decide later")}
                            </Button>
                        </>
                    }
                    {takingLonger && (
                        <Typography
                            component="p"
                            variant="body-regular"
                            role="status"
                            aria-live="polite"
                            sx={{ textAlign: "center" }}
                        >
                            {creationType === "adopt" ?
                                t(
                                    "action_plan.invitation.taking_longer",
                                    "Still working… Copying the actions and calculating your trajectory can take up to a minute. Please keep this window open.",
                                )
                            :   t(
                                    "action_plan.creation.taking_longer_own",
                                    "Still working… Creating your action plan can take up to a minute. Please keep this window open.",
                                )
                            }
                        </Typography>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
